import { DaysIndicator } from '@/components/displays/DaysIndicator';
import { InitialalsAvatar } from '@/components/displays/InitialsAvatar';
import { CountingButton } from '@/components/inputs/CountingButton/CountingButton';
import { HomeworkData, HomeworkStatus } from '@/pages/Homework/types/homework';
import { generateAvatarColor, getInitials } from '@/utils/avatar';
import { formatDatePart, formatTimePart } from '@/utils/format.date';
import DeleteIcon from '@mui/icons-material/Delete';
import { Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridFilterInputDate, GridFilterInputValue, GridFilterModel, GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import EmptyHomeworkManageTable from './EmptyTableHomeworkManager';
import { HomeworkManagerTableToolbar } from './HomeworkManagerTableToolbar';

export interface HomeworkManagerTableProps {
    rows: { count: number; rows: HomeworkData[] } | undefined;
    onQueryOptionUpdate: React.Dispatch<
        React.SetStateAction<
            | {
                  pagination?: GridPaginationModel;
                  sort?: GridSortModel;
                  filter?: GridFilterModel;
              }
            | undefined
        >
    >;
    onDeleteHomework: (id: string[]) => void;
    onCreateHomework: () => void;
    loading: boolean;
}

function getHomeworkStatus(status: HomeworkStatus) {
    switch (status) {
        case HomeworkStatus.archived:
            return 'Archived';
        case HomeworkStatus.started:
            return 'Started';
        case HomeworkStatus.ended:
            return 'Ended';
        default:
            return '';
    }
}

export default function HomeworkManagerTable(props: HomeworkManagerTableProps) {
    const { t } = useTranslation();
    const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>({
        type: 'include',
        ids: new Set(),
    });

    function convertStatusToLocale(status: HomeworkStatus) {
        switch (status) {
            case HomeworkStatus.archived:
                return t('homework.statusArchived');
            case HomeworkStatus.ended:
                return t('homework.statusEnded');
            case HomeworkStatus.started:
                return t('homework.statusStarted');
        }
    }

    const columns: GridColDef<HomeworkData>[] = [
        {
            field: 'startDate',
            headerName: t('homework.startDate'),
            minWidth: 160,
            sortable: false,
            filterable: true,
            filterOperators: [
                {
                    label: 'After',
                    value: 'gte',
                    InputComponent: GridFilterInputDate,
                    InputComponentProps: { type: 'date' },
                    getApplyFilterFn: () => {
                        return () => true;
                    },
                },
                {
                    label: 'before',
                    value: 'lte',
                    InputComponent: GridFilterInputDate,
                    InputComponentProps: { type: 'date' },
                    getApplyFilterFn: () => {
                        return () => true;
                    },
                },
            ],
            valueGetter: (value, row) => row.startTime,
            renderCell: (params) => {
                return (
                    <Stack direction='row' spacing={1} alignItems='center' height='100%'>
                        <DaysIndicator date={params.row.startTime}>
                            <Typography variant='body2'>{formatDatePart(params.row.startTime)}</Typography>
                        </DaysIndicator>
                    </Stack>
                );
            },
        },
        {
            field: 'startTime',
            headerName: t('homework.startTime'),
            minWidth: 160,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return formatTimePart(params.row.startTime);
            },
        },
        {
            field: 'endDate',
            headerName: t('homework.deadlineDate'),
            minWidth: 160,
            sortable: false,
            filterable: true,
            filterOperators: [
                {
                    label: 'After',
                    value: 'gte',
                    InputComponent: GridFilterInputDate,
                    InputComponentProps: { type: 'date' },
                    getApplyFilterFn: () => {
                        return () => true;
                    },
                },
                {
                    label: 'before',
                    value: 'lte',
                    InputComponent: GridFilterInputDate,
                    InputComponentProps: { type: 'date' },
                    getApplyFilterFn: () => {
                        return () => true;
                    },
                },
            ],
            valueGetter: (value, row) => row.endTime,
            renderCell: (params) => {
                return (
                    <Stack direction='row' spacing={1} alignItems='center' height='100%'>
                        <DaysIndicator date={params.row.endTime}>
                            <Typography variant='body2'>{formatDatePart(params.row.endTime)}</Typography>
                        </DaysIndicator>
                    </Stack>
                );
            },
        },
        {
            field: 'deadlineTime',
            headerName: t('homework.deadlineTime'),
            minWidth: 160,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return formatTimePart(params.row.endTime);
            },
        },
        {
            field: 'lessonName',
            headerName: t('homework.lessonName'),
            minWidth: 160,
            sortable: false,
            filterable: true,
            flex: 1,
            filterOperators: [
                {
                    label: 'contains',
                    value: 'contains',
                    getApplyFilterFn: () => {
                        return () => {
                            return true;
                        };
                    },
                    InputComponentProps: { type: 'string' },
                    InputComponent: GridFilterInputValue,
                },
            ],
        },
        {
            field: 'homeworkName',
            headerName: t('homework.homeworkName'),
            minWidth: 160,
            sortable: false,
            filterable: true,
            flex: 1,
            filterOperators: [
                {
                    label: 'contains',
                    value: 'contains',
                    getApplyFilterFn: () => {
                        return () => {
                            return true;
                        };
                    },
                    InputComponentProps: { type: 'string' },
                    InputComponent: GridFilterInputValue,
                },
            ],
        },
        {
            field: 'classes',
            headerName: t('homework.class'),
            sortable: false,
            minWidth: 200,
            // FIXME: filter doesn't work
            filterable: false,
            flex: 2,
            renderCell: (params) => {
                return (
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{
                            height: '100%',
                        }}
                    >
                        {params.row.classes.map((data, index: number) => {
                            const name = data.className;
                            const avatarColor = generateAvatarColor(name);
                            const bg = avatarColor?.bg;
                            const text = avatarColor?.text;

                            return (
                                <Stack key={index} direction='row' spacing={1} alignItems='center'>
                                    <Chip
                                        size='medium'
                                        sx={{
                                            '& .MuiChip-avatar': {
                                                color: text,
                                            },
                                        }}
                                        avatar={
                                            <InitialalsAvatar
                                                sx={{
                                                    bgcolor: bg,
                                                }}
                                            >
                                                {getInitials(name)}
                                            </InitialalsAvatar>
                                        }
                                        label={`${data.className}${data.schoolYear ? ` (${data.schoolYear})` : ''}`}
                                    />
                                </Stack>
                            );
                        })}
                    </Stack>
                );
            },
            filterOperators: [
                {
                    label: 'contains',
                    value: 'contains',
                    getApplyFilterFn: () => {
                        return () => {
                            return true;
                        };
                    },
                    InputComponentProps: { type: 'string' },
                    InputComponent: GridFilterInputValue,
                },
            ],
        },
        {
            field: 'status',
            headerName: t('homework.status'),
            sortable: false,
            // FIXME: filter doesn't work
            filterable: false,
            minWidth: 120,
            renderCell: (params) => {
                return (
                    <Typography
                        sx={{
                            fontSize: '0.875em',
                            color: (theme) => (getHomeworkStatus(params.row.status) === 'Started' ? (theme.vars || theme).palette.success.main : (theme.vars || theme).palette.error.main),
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        {convertStatusToLocale(params.row.status)}
                    </Typography>
                );
            },
            filterOperators: [
                {
                    label: 'contains',
                    value: 'contains',
                    getApplyFilterFn: () => {
                        return () => {
                            return true;
                        };
                    },
                    InputComponentProps: { type: 'string' },
                    InputComponent: GridFilterInputValue,
                },
            ],
        },
        {
            field: 'delete',
            type: 'actions',
            headerName: t('common.actions'),
            sortable: false,
            filterable: false,
            align: 'center',
            renderCell: (params) => {
                return (
                    <Tooltip title={t('common.delete')}>
                        <IconButton
                            aria-label='delete-homework'
                            onClick={(event) => {
                                event.stopPropagation();
                                props.onDeleteHomework([params.row.id]);
                            }}
                        >
                            <DeleteIcon color='error' fontSize='small' />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <DataGrid
            columns={columns}
            autoPageSize
            filterMode='server'
            sortingMode='server'
            paginationMode='server'
            loading={props.loading}
            onPaginationModelChange={(model) => {
                props?.onQueryOptionUpdate((prev) => (prev ? { ...prev, pagination: model } : { pagination: model }));
            }}
            onSortModelChange={(model) => {
                props?.onQueryOptionUpdate((prev) => (prev ? { ...prev, sort: model } : { sort: model }));
            }}
            onFilterModelChange={(model) => {
                props?.onQueryOptionUpdate((prev) => (prev ? { ...prev, filter: model } : { filter: model }));
            }}
            rows={props?.rows?.rows || []} // Provide fallback empty array
            rowCount={props?.rows?.count || 0}
            slots={{
                toolbar: (toolBarProps) => <HomeworkManagerTableToolbar {...toolBarProps} />,
                noRowsOverlay: () => <EmptyHomeworkManageTable onOpen={props.onCreateHomework} />,
            }}
            showToolbar
            slotProps={{
                loadingOverlay: {
                    variant: 'linear-progress',
                    noRowsVariant: 'linear-progress',
                },
                toolbar: {
                    additionalTool: {
                        right: (
                            <CountingButton
                                variant='text'
                                count={rowSelectionModel.ids.size}
                                endIcon={<DeleteIcon />}
                                onClick={() => {
                                    if (rowSelectionModel.ids.size === 0) return;
                                    props.onDeleteHomework(Array.from(rowSelectionModel.ids).map((id) => id.toString()));
                                }}
                            >
                                {t('common.delete')}
                            </CountingButton>
                        ),
                    },
                },
            }}
            onRowSelectionModelChange={(model) => {
                setRowSelectionModel(model);
            }}
            checkboxSelection
            keepNonExistentRowsSelected
        />
    );
}
